import { motion } from "framer-motion";
import { Clock, MapPin, CalendarX } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { Reveal, ChapterLabel } from "@/components/Reveal";
import { upcomingEvents } from "@/lib/events";

const EASE = [0.22, 1, 0.36, 1];

export default function Events() {
  const { content } = useSite();
  const events = upcomingEvents(content.events);

  return (
    <main data-testid="events-page">
      <section className="relative bg-navy grain pt-40 pb-24 overflow-hidden" data-testid="events-header">
        <div className="max-w-7xl mx-auto px-6">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-[11px] tracking-[0.35em] uppercase font-bold text-brand-sky mb-6"
          >
            Drywall Local 675
          </motion.p>
          <h1 className="font-heading font-bold uppercase text-white text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none" data-testid="events-title">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
              >
                Upcoming events
              </motion.span>
            </span>
          </h1>
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="events-timeline-section">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <ChapterLabel number="01" label="Dates to remember" testid="timeline-chapter" />
          </Reveal>
          <ol className="relative border-l-2 border-brand/25 ml-2" data-testid="events-timeline">
            {events.length === 0 && (
              <li className="relative pl-10 text-sm text-slate-500" data-testid="events-empty">
                No upcoming events right now — check back soon.
              </li>
            )}
            {events.map((event, i) => (
              <li key={i} className="relative pl-10 pb-14 last:pb-0">
                <span className="absolute -left-[9px] top-2 h-4 w-4 rounded-full bg-brand ring-4 ring-brand-light" />
                <Reveal delay={i * 0.06}>
                  <div className="group" data-testid={`event-item-${i}`}>
                    <p className="text-xs tracking-[0.25em] uppercase font-bold text-brand mb-2">{event.date}</p>
                    <h3 className="font-heading text-2xl sm:text-3xl uppercase text-slate-900 group-hover:text-brand transition-colors duration-300 mb-3">
                      {event.title}
                    </h3>
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                      {event.time && (
                        <span className="inline-flex items-center gap-2 text-sm text-slate-600">
                          <Clock className="h-4 w-4 text-brand" />
                          {event.time}
                        </span>
                      )}
                      {event.location && (
                        <span className="inline-flex items-center gap-2 text-sm text-slate-600">
                          <MapPin className="h-4 w-4 text-brand" />
                          {event.location}
                        </span>
                      )}
                    </div>
                  </div>
                </Reveal>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-24 sm:py-28 bg-slate-50" data-testid="closures-section">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <ChapterLabel number="02" label="Office closures" testid="closures-chapter" />
          </Reveal>
          <div className="border border-slate-200 bg-white divide-y divide-slate-200" data-testid="closures-list">
            {content.closures.map((closure, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="flex items-center gap-5 px-8 py-6" data-testid={`closure-item-${i}`}>
                  <span className="grid place-items-center h-11 w-11 bg-brand-light text-brand shrink-0">
                    <CalendarX className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-heading text-lg uppercase text-slate-900">{closure.title}</p>
                    <p className="text-sm text-slate-500">{closure.date}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
